import { forwardRef, useImperativeHandle, useEffect, useRef, useState, useCallback } from "react";
import { useLocation } from "wouter";
import { Building2, FolderOpen, CheckSquare, User, Clock, Loader2 } from "lucide-react";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useQuery } from "@tanstack/react-query";
import { globalSearch, getRecentClients, type SearchResult } from "@/lib/api";

export interface SearchPanelHandle {
  handleKeyDown: (e: React.KeyboardEvent) => void;
}

interface SearchPanelProps {
  query: string;
  onClose: () => void;
}

const TYPE_META: Record<string, { label: string; icon: React.ReactNode }> = {
  client:  { label: "Client",  icon: <Building2 className="w-3.5 h-3.5" /> },
  project: { label: "Project", icon: <FolderOpen className="w-3.5 h-3.5" /> },
  task:    { label: "Task",    icon: <CheckSquare className="w-3.5 h-3.5" /> },
  contact: { label: "Contact", icon: <User className="w-3.5 h-3.5" /> },
};

const TYPE_ORDER: SearchResult["type"][] = ["client", "project", "task", "contact"];

const TAB_TYPE_MAP: Record<string, SearchResult["type"][]> = {
  all:      ["client", "project", "task", "contact"],
  clients:  ["client"],
  projects: ["project"],
  tasks:    ["task"],
};

export const SearchPanel = forwardRef<SearchPanelHandle, SearchPanelProps>(
  function SearchPanel({ query, onClose }, ref) {
    const [, navigate] = useLocation();
    const [results, setResults] = useState<SearchResult[]>([]);
    const [loading, setLoading] = useState(false);
    const [activeIdx, setActiveIdx] = useState(0);
    const [activeTab, setActiveTab] = useState("all");
    const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const { data: recentClients = [] } = useQuery({
      queryKey: ["recent-clients"],
      queryFn: getRecentClients,
      staleTime: 60_000,
    });

    // Run search when query changes
    useEffect(() => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      if (query.length < 2) { setResults([]); setLoading(false); return; }
      setLoading(true);
      debounceRef.current = setTimeout(async () => {
        const r = await globalSearch(query);
        setResults(r);
        setActiveIdx(0);
        setLoading(false);
      }, 300);
    }, [query]);

    // Reset on query clear
    useEffect(() => {
      if (query === "") { setResults([]); setActiveIdx(0); }
    }, [query]);

    const allowedTypes = TAB_TYPE_MAP[activeTab] ?? TYPE_ORDER;
    const filtered = results.filter((r) => allowedTypes.includes(r.type));
    const grouped = TYPE_ORDER
      .filter((t) => allowedTypes.includes(t))
      .map((type) => ({ type, items: filtered.filter((r) => r.type === type) }))
      .filter((g) => g.items.length > 0);

    const handleSelect = useCallback((result: SearchResult) => {
      onClose();
      if (result.type === "client") navigate(`/accounts/${result.id}`);
      else if (result.type === "project" && result.accountId) navigate(`/accounts/${result.accountId}?openProject=${result.id}`);
      else if (result.type === "task" && result.accountId) navigate(`/accounts/${result.accountId}?${result.projectId ? `openProject=${result.projectId}&` : ""}openTask=${result.id}`);
      else if (result.accountId) navigate(`/accounts/${result.accountId}`);
    }, [navigate, onClose]);

    const handleRecentSelect = useCallback((id: string) => {
      onClose();
      navigate(`/accounts/${id}`);
    }, [navigate, onClose]);

    const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setActiveIdx((i) => Math.min(i + 1, filtered.length - 1));
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        setActiveIdx((i) => Math.max(i - 1, 0));
      } else if (e.key === "Enter" && filtered[activeIdx]) {
        handleSelect(filtered[activeIdx]);
      } else if (e.key === "Escape") {
        onClose();
      }
    }, [filtered, activeIdx, handleSelect, onClose]);

    useImperativeHandle(ref, () => ({ handleKeyDown }), [handleKeyDown]);

    const showRecentClients = query.length < 2 && (activeTab === "all" || activeTab === "clients");
    const showTypePrompt = query.length < 2 && activeTab !== "all" && activeTab !== "clients";

    let flatIdx = 0;

    return (
      <div className="w-[520px] rounded-xl border bg-popover shadow-xl overflow-hidden">
        <Tabs value={activeTab} onValueChange={(v) => { setActiveTab(v); setActiveIdx(0); }}>
          <TabsList className="w-full justify-start rounded-none border-b bg-transparent px-3 h-9 gap-0">
            {["all", "clients", "projects", "tasks"].map((tab) => (
              <TabsTrigger
                key={tab}
                value={tab}
                className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent px-3 capitalize text-xs h-9"
              >
                {tab === "all" ? "All" : tab.charAt(0).toUpperCase() + tab.slice(1)}
              </TabsTrigger>
            ))}
            {loading && <Loader2 className="w-3.5 h-3.5 ml-auto mr-1 animate-spin text-muted-foreground self-center" />}
          </TabsList>

          <div className="max-h-80 overflow-y-auto py-1">
            {/* Recent Clients (empty state for All + Clients tabs) */}
            {showRecentClients && recentClients.length > 0 && (
              <div>
                <div className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                  <Clock className="w-3.5 h-3.5" />
                  Recent Clients
                </div>
                {recentClients.map((c) => (
                  <button
                    key={c.id}
                    className="w-full text-left px-3 py-2 flex items-center gap-2 text-sm hover:bg-accent/50 transition-colors"
                    onClick={() => handleRecentSelect(c.id)}
                  >
                    <Building2 className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
                    <span className="truncate">{c.account_name}</span>
                    {c.entity_type && (
                      <span className="ml-auto text-xs text-muted-foreground shrink-0">{c.entity_type}</span>
                    )}
                  </button>
                ))}
              </div>
            )}

            {/* Type-specific empty state for Projects/Tasks tabs */}
            {showTypePrompt && (
              <p className="text-xs text-muted-foreground text-center py-6">
                Type to search {activeTab}…
              </p>
            )}

            {/* No results */}
            {query.length >= 2 && !loading && filtered.length === 0 && (
              <p className="text-xs text-muted-foreground text-center py-6">No results for "{query}"</p>
            )}

            {/* Grouped results */}
            {grouped.map(({ type, items }) => {
              const meta = TYPE_META[type];
              return (
                <div key={type}>
                  <div className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                    {meta.icon}
                    {meta.label}s
                  </div>
                  {items.map((item) => {
                    const idx = flatIdx++;
                    const isActive = idx === activeIdx;
                    return (
                      <button
                        key={item.id}
                        className={`w-full text-left px-3 py-2 flex items-start gap-2 text-sm transition-colors ${
                          isActive ? "bg-accent text-accent-foreground" : "hover:bg-accent/50"
                        }`}
                        onMouseEnter={() => setActiveIdx(idx)}
                        onClick={() => handleSelect(item)}
                      >
                        <span className="text-muted-foreground mt-0.5 shrink-0">{meta.icon}</span>
                        <div className="min-w-0">
                          <p className="truncate font-medium">{item.title}</p>
                          <p className="text-xs text-muted-foreground truncate">{item.subtitle}</p>
                        </div>
                      </button>
                    );
                  })}
                </div>
              );
            })}
          </div>
        </Tabs>

        <div className="border-t px-3 py-2 flex items-center gap-3 text-xs text-muted-foreground">
          <span><kbd className="px-1 py-0.5 rounded border text-xs">↑↓</kbd> navigate</span>
          <span><kbd className="px-1 py-0.5 rounded border text-xs">Enter</kbd> select</span>
          <span><kbd className="px-1 py-0.5 rounded border text-xs">Esc</kbd> close</span>
        </div>
      </div>
    );
  }
);
